/**
 * useAssetsData — load the full asset set + industry universe for the Overview.
 *
 * Fetches `ListAssets({})` (ALL origins — NOT scoped to ivt_incubated like the old
 * New Assets page) + `ListIndustries` in parallel. 4-state (loading / error / empty
 * / data) with a cancelled-guard and a manual reload key. Aggregation (KPIs /
 * distribution / grouping / origin+search filtering) is done by the page from the
 * returned full set — this hook only owns fetching.
 */
import { useEffect, useState } from 'react';
import {
  getIvtClient,
  ListAssetsCommand,
  ListIndustriesCommand,
  type AssetSummary,
  type IndustryMeta,
} from '../../../client/IvtHubClient';

export interface AssetsData {
  assets: AssetSummary[] | null;
  industries: IndustryMeta[] | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useAssetsData(): AssetsData {
  const [assets, setAssets] = useState<AssetSummary[] | null>(null);
  const [industries, setIndustries] = useState<IndustryMeta[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const client = getIvtClient();
        const [assetsOut, industriesOut] = await Promise.all([
          // NO origin filter — Overview shows all origins (new + legacy + partner +
          // global); origin is used only for the "New Assets" filter + green bar.
          client.send(new ListAssetsCommand({})),
          client.send(new ListIndustriesCommand({})),
        ]);
        if (cancelled) return;
        setAssets(assetsOut.assets ?? []);
        setIndustries(industriesOut.industries ?? []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return { assets, industries, loading, error, reload: () => setReloadKey((k) => k + 1) };
}
